import { useState, useEffect, useCallback } from "react";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { navItems, salonInfo } from "@/mocks/content";

interface CtaButtonProps {
  phone: string;
  size?: "sm" | "md";
}

function DesktopCtaButton({ phone }: CtaButtonProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={`tel:${phone}`}
      className="inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
      style={{
        fontFamily: "'Noto Sans JP', sans-serif",
        fontSize: "0.72rem",
        letterSpacing: "0.22em",
        fontWeight: 400,
        color: hovered ? "#E8C49A" : "#D4A373",
        textDecoration: "none",
        transition: "color 0.3s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="flex items-center justify-center w-4 h-4" style={{ opacity: hovered ? 1 : 0.7, transition: "opacity 0.3s" }}>
        <Phone size={12} />
      </span>
      <span className="relative pb-px">
        予約する
        <span
          className="absolute bottom-0 left-0 h-px bg-[#D4A373]"
          style={{ width: hovered ? "100%" : "0%", transition: "width 0.35s cubic-bezier(0.23, 1, 0.32, 1)" }}
        />
      </span>
      <span
        className="flex items-center justify-center w-3 h-3"
        style={{ transform: hovered ? "translateX(4px)" : "translateX(0)", opacity: hovered ? 1 : 0.5, transition: "transform 0.3s ease, opacity 0.3s ease" }}
      >
        <ArrowRight size={11} />
      </span>
    </a>
  );
}

function MobileCtaButton({ phone }: CtaButtonProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={`tel:${phone}`}
      className="inline-flex items-center gap-3 whitespace-nowrap cursor-pointer"
      style={{
        fontFamily: "'Noto Sans JP', sans-serif",
        fontSize: "0.9rem",
        letterSpacing: "0.2em",
        fontWeight: 400,
        color: hovered ? "#E8C49A" : "#D4A373",
        textDecoration: "none",
        transition: "color 0.3s ease",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="flex items-center justify-center w-5 h-5" style={{ opacity: hovered ? 1 : 0.7, transition: "opacity 0.3s" }}>
        <Phone size={15} />
      </span>
      <span className="relative pb-px">
        {phone}
        <span
          className="absolute bottom-0 left-0 h-px bg-[#D4A373]"
          style={{ width: hovered ? "100%" : "0%", transition: "width 0.35s cubic-bezier(0.23, 1, 0.32, 1)" }}
        />
      </span>
      <span
        className="flex items-center justify-center w-4 h-4"
        style={{ transform: hovered ? "translateX(5px)" : "translateX(0)", opacity: hovered ? 1 : 0.5, transition: "transform 0.3s ease, opacity 0.3s ease" }}
      >
        <ArrowRight size={13} />
      </span>
    </a>
  );
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleNavClick = useCallback(
    (href: string) => {
      setIsOpen(false);
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 400);
    },
    []
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#D4A373]/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="flex flex-col leading-none cursor-pointer"
          >
            <span
              className="text-[#D4A373] tracking-[0.3em] text-xs font-light"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              MENS SPA
            </span>
            <span
              className="text-white tracking-[0.15em] text-lg md:text-xl font-medium"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              REFINE
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.href);
                }}
                className="relative text-white/70 hover:text-[#D4A373] text-sm tracking-widest transition-colors duration-300 group cursor-pointer"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#D4A373] group-hover:w-full transition-all duration-400" />
              </a>
            ))}
            {/* Desktop CTA — minimal text button */}
            <DesktopCtaButton phone={salonInfo.phone} />
          </nav>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer z-50 relative"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="メニューを開く"
          >
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-600 ${
          isOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{
          clipPath: isOpen
            ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
            : "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)",
          transition: "clip-path 0.6s cubic-bezier(0.77, 0, 0.175, 1)",
          background: "linear-gradient(135deg, #0A0A0A 0%, #1A1208 50%, #0A0A0A 100%)",
        }}
      >
        {/* Diagonal accent line */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ opacity: isOpen ? 1 : 0, transition: "opacity 0.4s 0.3s" }}
        >
          <div
            className="absolute w-px bg-gradient-to-b from-transparent via-[#D4A373]/30 to-transparent"
            style={{
              height: "120%",
              top: "-10%",
              left: "30%",
              transform: "rotate(15deg)",
            }}
          />
        </div>

        <div className="flex flex-col items-center justify-center h-full gap-10 px-8">
          <div
            className="text-[#D4A373] tracking-[0.4em] text-xs mb-4"
            style={{
              fontFamily: "'Noto Sans JP', sans-serif",
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.4s 0.3s",
            }}
          >
            NAVIGATION
          </div>
          {navItems.map((item, i) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => {
                e.preventDefault();
                handleNavClick(item.href);
              }}
              className="text-white text-2xl tracking-[0.2em] hover:text-[#D4A373] transition-colors duration-300 cursor-pointer"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                opacity: isOpen ? 1 : 0,
                transform: isOpen ? "translateY(0)" : "translateY(30px)",
                transition: `all 0.5s ${0.35 + i * 0.08}s cubic-bezier(0.23, 1, 0.32, 1)`,
              }}
            >
              {item.label}
            </a>
          ))}
          {/* Mobile CTA — minimal text button */}
          <div
            style={{
              opacity: isOpen ? 1 : 0,
              transform: isOpen ? "translateY(0)" : "translateY(30px)",
              transition: `all 0.5s ${0.35 + navItems.length * 0.08}s cubic-bezier(0.23, 1, 0.32, 1)`,
            }}
          >
            <MobileCtaButton phone={salonInfo.phone} />
          </div>
        </div>
      </div>
    </>
  );
}
